import React, { useCallback, useEffect, useState } from 'react';
import { authFetch } from '../shopify/authFetch';
import { redirectToShopifyBilling } from '../utils/billingRedirect';
import './TrialStatusNotice.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TrialStatusNotice({ shop, onOpenBilling }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(false);

  const refresh = useCallback(async () => {
    if (!shop || !BACKEND_URL) return;
    try {
      const response = await authFetch(`${BACKEND_URL}/api/usage/current?shop=${encodeURIComponent(shop)}`, {
        method: 'GET',
        cache: 'no-store'
      });
      if (!response.ok) throw new Error('Trial status unavailable');
      const data = await response.json();
      // A transient API failure must never be presented as an expired trial.
      if (!['paid', 'trial_active', 'trial_expired', 'inactive'].includes(data.effective_status)) {
        throw new Error('Unknown trial status');
      }
      setStatus(data);
      setError(false);
    } catch (err) {
      setError(true);
    }
  }, [shop]);

  useEffect(() => {
    refresh();
    const onFocus = () => refresh();
    const onVisible = () => { if (!document.hidden) refresh(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  if (!shop || !status || error || status.effective_status === 'paid') return null;
  const trial = status.trial;
  const active = status.effective_status === 'trial_active';
  const expired = status.effective_status === 'trial_expired';
  const remaining = trial?.ends_at ? Math.max(0, Math.ceil((new Date(trial.ends_at).getTime() - Date.now()) / 86400000)) : null;
  const title = active ? 'Your free trial is active' : expired ? 'Your free trial has ended' : 'Choose a plan to activate Tax & Duty King';
  const message = active
    ? remaining === null
      ? 'Explore Tax & Duty King during your free trial. Choose a plan before your trial ends to keep calculations running.'
      : remaining <= 3
        ? `Your trial ends in ${remaining} ${remaining === 1 ? 'day' : 'days'}. Choose a plan to keep your calculators running without interruption.`
        : `You have ${remaining} days left to explore Tax & Duty King. Choose a plan before your trial ends to keep calculations running.`
    : expired
      ? 'Your trial has ended and calculations are currently unavailable. Choose a subscription plan to restore access. Your configuration is saved.'
      : 'An active trial or subscription is required for calculations. Select a plan to start using Tax & Duty King.';

  const choosePlan = () => {
    if (!redirectToShopifyBilling(shop) && onOpenBilling) onOpenBilling();
  };

  return (
    <section className={`tdk-trial-notice ${active ? 'tdk-trial-active' : 'tdk-trial-expired'}`} role={active ? 'status' : 'alert'} aria-live="polite">
      <div className="tdk-trial-copy">
        <strong>{title}</strong>
        <p>{message}</p>
      </div>
      <div className="tdk-trial-actions">
        <button type="button" onClick={choosePlan}>Choose a plan</button>
        {!active && <button type="button" className="tdk-trial-secondary" onClick={refresh}>I've subscribed, refresh</button>}
      </div>
    </section>
  );
}
